import { memo, useCallback, useMemo, useState } from 'react';
import { Pie, DatumId, PieCustomLayerProps, ComputedDatum } from '@nivo/pie';
import { numberToLocaleString, cn } from '@/lib/utils';
import { WifiOffIcon, Loader2Icon } from 'lucide-react';
import { useIsDarkMode } from '@/hooks/theme';
import DebouncedResizeContainer from "@/components/DebouncedResizeContainer";
import { useAtomValue } from 'jotai';
import { dashPlayerDropAtom, useGetDashDataAge } from './dashboardHooks';
import { playerDropCategories, playerDropCategoryDefaultColor } from '@/lib/playerDropCategories';

type PlayerDropChartDatum = {
    id: string;
    label: string;
    value: number;
    count: number;
}

type PieCenterTextProps = PieCustomLayerProps<PlayerDropChartDatum> & {
    active?: ComputedDatum<PlayerDropChartDatum>;
};

const PieCenterText = ({ centerX, centerY, dataWithArc, innerRadius, active }: PieCenterTextProps) => {
    if (active) {
        return (
            <>
                <circle
                    cx={centerX}
                    cy={centerY}
                    r={innerRadius * 0.9}
                    fill="rgba(7, 9, 16, 0.9)"
                    stroke={active.color}
                    strokeWidth="1.5"
                />
                <text
                    x={centerX}
                    y={centerY - 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className='text-[9px] uppercase font-bold tracking-widest fill-[#94a3b8]'
                >
                    {active.label}
                </text>
                <text
                    x={centerX}
                    y={centerY + 12}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className='text-lg font-bold font-mono'
                    fill={active.color}
                >
                    {numberToLocaleString(active.data.count)}
                </text>
            </>
        )
    } else {
        const totalDrops = useMemo(() => dataWithArc.reduce((acc, d) => acc + d.data.count, 0), [dataWithArc]);
        return (
            <>
                <circle
                    cx={centerX}
                    cy={centerY}
                    r={innerRadius * 0.9}
                    fill="rgba(7, 9, 16, 0.5)"
                    stroke="rgba(255, 255, 255, 0.05)"
                    strokeWidth="1"
                />
                <text
                    x={centerX}
                    y={centerY - 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className='text-[9px] uppercase font-bold tracking-widest fill-[#94a3b8]'
                >
                    Disconnects
                </text>
                <text
                    x={centerX}
                    y={centerY + 12}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className='text-lg font-bold fill-white font-mono'
                >
                    {numberToLocaleString(totalDrops)}
                </text>
            </>
        )
    }
}

type PlayerDropChartProps = {
    data: PlayerDropChartDatum[];
    activeId: DatumId | null;
    setActiveId: (id: DatumId | null) => void;
    width: number;
    height: number;
};

const PlayerDropChart = memo(({ data, activeId, setActiveId, width, height }: PlayerDropChartProps) => {
    const isDarkMode = useIsDarkMode();
    const [hasClicked, setHasClicked] = useState(false);
    const CenterLayer = useCallback((allArgs: PieCustomLayerProps<PlayerDropChartDatum>) => {
        if (!activeId) return PieCenterText(allArgs);
        const active = allArgs.dataWithArc.find(d => d.id === activeId);
        return PieCenterText({ ...allArgs, active });
    }, [activeId]);

    if (!width || !height) return null;
    return (
        <Pie
            data={data}
            height={height}
            width={width}
            theme={{
                text: {
                    fontSize: '11px',
                    fontWeight: 600,
                    fill: '#94a3b8'
                },
            }}
            activeId={activeId}
            onActiveIdChange={setActiveId}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            innerRadius={0.65}
            padAngle={2.5}
            cornerRadius={5}
            activeOuterRadiusOffset={4}
            borderWidth={1}
            borderColor={isDarkMode ? 'rgba(255,255,255,0.05)' : undefined}
            valueFormat={'.1%'}
            enableArcLinkLabels={false}
            layers={['arcs', 'arcLabels', 'arcLinkLabels', CenterLayer]}
            arcLabelsSkipAngle={35}
            arcLabelsTextColor="#ffffff"
            onClick={(datum) => setHasClicked(curr => !curr)}
            onMouseEnter={(datum) => setHasClicked(false)}
            onMouseLeave={(datum, event) => {
                hasClicked && setActiveId(datum.id)
                event.preventDefault()
            }}
            colors={{ datum: 'data.color' }}
            tooltip={() => null}
            sortByValue
        />
    )
});

const normalizeCategoryKey = (reason: string) => {
    const key = reason.toLowerCase();
    if (key === 'quit') return 'player';
    if (key === 'kick') return 'resource';
    return key;
};

export default function PlayerDropCard() {
    const [activeId, setActiveId] = useState<DatumId | null>(null)
    const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
    const playerDropData = useAtomValue(dashPlayerDropAtom);
    const getDashDataAge = useGetDashDataAge();

    const chartData = useMemo(() => {
        if (!playerDropData?.summaryLast6h) return null;
        const dataAge = getDashDataAge();
        if (dataAge.isExpired) return null;
        if (!playerDropData.summaryLast6h.length) return 'not_enough_data';

        const totalDrops = playerDropData.summaryLast6h.reduce((acc, d) => acc + d[1], 0);
        return playerDropData.summaryLast6h.map(([reason, count]) => {
            const normalizedKey = normalizeCategoryKey(reason);
            const categoryInfo = playerDropCategories[normalizedKey];
            return {
                id: reason,
                label: categoryInfo?.label ?? reason,
                count,
                value: count / totalDrops,
                color: categoryInfo?.color ?? playerDropCategoryDefaultColor,
                border: categoryInfo?.border ?? playerDropCategoryDefaultColor,
            };
        });
    }, [playerDropData?.summaryLast6h]);

    const displayLegends = useMemo(() => {
        if (!playerDropData?.summaryLast6h) return null;
        const dataAge = getDashDataAge();
        if (dataAge.isExpired) return null;
        if (!playerDropData.summaryLast6h.length) return null;

        return playerDropData.summaryLast6h.map(([reason, count]) => {
            const normalizedKey = normalizeCategoryKey(reason);
            const categoryInfo = playerDropCategories[normalizedKey];
            return {
                id: reason,
                label: categoryInfo?.label ?? reason,
                count,
                color: categoryInfo?.color ?? playerDropCategoryDefaultColor,
                border: categoryInfo?.border ?? playerDropCategoryDefaultColor,
            };
        });
    }, [playerDropData?.summaryLast6h]);

    let contentNode: React.ReactNode = null;
    if (typeof chartData === 'object' && chartData !== null) {
        contentNode = (
            <PlayerDropChart
                data={chartData}
                activeId={activeId}
                setActiveId={setActiveId}
                width={chartSize.width}
                height={chartSize.height}
            />
        );
    } else if (typeof chartData === 'string') {
        contentNode = (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground text-center p-4">
                <p className='text-xs leading-relaxed max-w-72'>
                    No player disconnect events registered in the last 6 hours.
                </p>
            </div>
        );
    } else {
        contentNode = (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Loader2Icon className="animate-spin size-12 text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="col-span-3 sm:col-span-2 2xl:col-span-3 py-2 px-4 md:rounded-xl border bg-card shadow-sm flex flex-col h-[20rem] max-h-[20rem]">
            <div className="flex flex-row items-center justify-between pb-2 text-muted-foreground">
                <h3 className="tracking-tight text-sm font-semibold flex items-center gap-1.5">
                    <WifiOffIcon size={14} className="text-scCyan" />
                    Disconnect Diagnostics
                </h3>
                <span className="text-[10px] font-mono text-muted/60">LAST 6H</span>
            </div>

            <div className="flex-1 min-h-0 relative">
                <DebouncedResizeContainer onDebouncedResize={setChartSize}>
                    {contentNode}
                </DebouncedResizeContainer>
            </div>

            {displayLegends && (
                <div className='mx-auto flex flex-wrap justify-center gap-1.5 pt-2 border-t border-white/[0.03] mt-1'>
                    {displayLegends.map(legend => {
                        const isActive = activeId === legend.id;
                        return (
                            <button
                                key={legend.id}
                                className={cn(
                                    "flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-transparent transition-all duration-150",
                                    "bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/5",
                                    isActive && "bg-white/[0.08] border-white/10"
                                )}
                                onClick={() => setActiveId(isActive ? null : legend.id)}
                            >
                                <div
                                    className="size-2 rounded-full shadow-inner shrink-0"
                                    style={{ backgroundColor: legend.color }}
                                />
                                <span className="text-[10px] text-slate-300 font-semibold">{legend.label}</span>
                                <span className="text-[10px] text-muted-foreground font-mono font-bold ml-0.5">
                                    ({legend.count})
                                </span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    );
}
